<template>
  <div class="absence-reasons text-dark pad-1">
    <div class="absence-reasons__header">
      <h2>{{ i18n['absence_reasons'] }}</h2>
      <button class="do-bttn do-bttn--submit"
              @click="setIsCreateAbsenceReason(true)"
              :content="'<p class=' + 'tooltip-shortcut' + '>' + $t('vue2.common.press') + '<span>N</span></p>'"
              v-tippy="{
                    arrow: true,
                    placement : 'bottom'
                  }">
        {{ i18n['create_absence'] }}
      </button>
    </div>
    <Loader v-if="isLoading"></Loader>
    <template v-else>
      <h2 class="text-explainer" v-if="absenceReasonsActive.length === 0">{{ i18n['no_absence_reasons_created'] }}</h2>
      <AbsenceReasonsTable
        v-else
        :items="absenceReasonsActive"
        :isAbsenceRequestActivated="isAbsenceRequestActivated"
        :isAbsenceIntegrationActive="isAbsenceIntegrationActive"
        :absenceCodes="absenceCodes"
        :sdAbsenceCodes="sdAbsenceCodes"
        :salarySystem="salarySystem"
        @editAbsencePrivate="editAbsencePrivate"
        @editAbsenceCanRequest="editAbsenceCanRequest"
        @editAbsenceOngoing="editAbsenceOngoing"
        @editAbsenceCode="editAbsenceCode"
        @toggleArchived="toggleArchived">
      </AbsenceReasonsTable>
      <p class="text-link marg-1-top" @click="isShowArchived = !isShowArchived"
         v-if="absenceReasonsArchived.length > 0">{{ isShowArchived ? i18n['hide_archived'] : i18n['show_archived'] }}
        {{ '(' + absenceReasonsArchived.length + ')' }}</p>
      <AbsenceReasonsTable
        v-if="isShowArchived && absenceReasonsArchived.length > 0"
        :items="absenceReasonsArchived"
        :isAbsenceRequestActivated="isAbsenceRequestActivated"
        :isAbsenceIntegrationActive="isAbsenceIntegrationActive"
        :absenceCodes="absenceCodes"
        :sdAbsenceCodes="sdAbsenceCodes"
        :salarySystem="salarySystem"
        @editAbsencePrivate="editAbsencePrivate"
        @toggleArchived="toggleArchived">
      </AbsenceReasonsTable>
    </template>
    <AbsenceReasonsCreate
      v-if="isCreateAbsenceReason"
      :absenceCodes="absenceCodes"
      :sdAbsenceCodes="sdAbsenceCodes"
      :isAbsenceIntegrationActive="isAbsenceIntegrationActive"
      :salarySystem="salarySystem"
      @create="handleCreateReason"
      @cancel="setIsCreateAbsenceReason(false)"
    />
  </div>
</template>

<script>
import docendo from '../../docendo'
import AppToggle from '../common/AppToggle.vue'
import Loader from '../common/Loader.vue'
import DModal from '../common/DModal.vue'
import AssignHoursEmploymentEnded from '../AssignHours/AssignHoursEmploymentEnded.vue'
import AbsenceReasonsTable from './AbsenceReasonsTable.vue'
import eventbus from '../../eventbus'
import AbsenceReasonsCreate from './AbsenceReasonsCreate.vue'

const I18N = {
  da: {
    absence_reasons: 'Fraværsårsager',
    create_absence: 'Opret fraværsårsag',
    title: 'Navn',
    need_a_title: 'Mangler et navn',
    no_absence_reasons_created: 'Ingen fraværsårsager oprettet',
    hide_archived: 'Skjul arkiverede',
    show_archived: 'Vis arkiverede',
    hide_title: 'Skjul titel'
  },
  nb: {
    absence_reasons: 'Fraværsårsaker',
    create_absence: 'Opprett fraværsårsak',
    title: 'Navn',
    need_a_title: 'Mangler et navn',
    no_absence_reasons_created: 'Ingen fraværsårsaker opprettet',
    hide_archived: 'Skjul arkiverte',
    show_archived: 'Vis arkiverte'
  },
  en: {
    absence_reasons: 'Absence reasons',
    create_absence: 'Create absence reason',
    title: 'Name',
    need_a_title: 'Missing a name',
    no_absence_reasons_created: 'No absence reasons created',
    hide_archived: 'Hide archived',
    show_archived: 'Show archived'
  }
}
export default {
  components: {AbsenceReasonsTable, AssignHoursEmploymentEnded, DModal, Loader, AppToggle, AbsenceReasonsCreate},
  data () {
    return {
      isLoading: false,
      absenceReasons: [],
      newReasonTitle: '',
      isCreateAbsenceReason: false,
      errors: {},
      isCreating: false,
      theUpdatingReasonId: null,
      _keyListener: null,
      isShowArchived: false,
      absenceCodes: [],
      sdAbsenceCodes: []
    }
  },
  directives: {
    focus: {
      // directive definition
      inserted: function (el) {
        setTimeout(_ => {
          el.focus()
        }, 150)
      }
    }
  },
  computed: {
    salarySystem () {
      return this.$store.getters.salarySystem
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    schoolFeatures () {
      return this.$store.getters.schoolFeatures
    },
    isAbsenceRequestActivated () {
      return this.schoolFeatures.indexOf('absence_requests') > -1
    },
    isAbsenceIntegrationActive () {
      return this.isOpusAbsenceActivated || this.isSDIntegrationActive
    },
    isOpusAbsenceActivated () {
      return this.schoolFeatures && this.schoolFeatures.indexOf('kmd_absence') > -1
    },
    isSDIntegrationActive () {
      return this.schoolFeatures && this.schoolFeatures.indexOf('silkeborg_data_integration') > -1
    },
    absenceReasonsSorted () {
      return this.absenceReasons.slice().sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
    },
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    absenceReasonsArchived () {
      return this.absenceReasonsSorted.filter(reason => reason.archived)
    },
    absenceReasonsActive () {
      return this.absenceReasonsSorted.filter(reason => !reason.archived)
    }
  },
  watch: {
    schoolId () {
      this.readAbsenceReasons()
      this.readAbsenceCodes()
    },
    salarySystem () {
      this.readAbsenceCodes()
    }
  },
  methods: {
    handleCreateReason (titleAndDefaultPrivate) {
      const title = titleAndDefaultPrivate.title
      const defaultPrivate = titleAndDefaultPrivate.defaultPrivate !== undefined ? titleAndDefaultPrivate.defaultPrivate : true
      const defaultOngoing = titleAndDefaultPrivate.defaultOngoing !== undefined ? titleAndDefaultPrivate.defaultOngoing : false
      const canRequest = titleAndDefaultPrivate.canRequest !== undefined ? titleAndDefaultPrivate.canRequest : true
      this.isCreating = true
      const plannedAbsence = false
      const kmdAbsenceCodeId = this.salarySystem === 'SD' ? null : (titleAndDefaultPrivate.kmdAbsenceCodeId || null)
      const sdAbsenceCodeId = this.salarySystem === 'SD' ? (titleAndDefaultPrivate.sdAbsenceCodeId || null) : null
      docendo.schoolAbsenceSubjectsCreate(this.schoolId, title, '#564437', defaultPrivate, defaultOngoing, plannedAbsence, kmdAbsenceCodeId, sdAbsenceCodeId, canRequest, success => {
        this.absenceReasons.push(success)
        this.setIsCreateAbsenceReason(false)
      }, failure => {
        console.log('error', failure)
      })
    },
    updateAbsenceReasonWithDefaults (reason, updates, errorMessage, trackEvent) {
      const plannedAbsence = reason.planned_absence || false
      const kmdAbsenceCodeId = updates.kmdAbsenceCodeId !== undefined ? updates.kmdAbsenceCodeId : (reason.kmd_absence_code_id || null)
      const sdAbsenceCodeId = updates.sdAbsenceCodeId !== undefined ? updates.sdAbsenceCodeId : (reason.sd_absence_code_id || null)
      const defaultPrivate = updates.defaultPrivate !== undefined ? updates.defaultPrivate : reason.default_private
      const defaultOngoing = updates.defaultOngoing !== undefined ? updates.defaultOngoing : reason.default_ongoing
      const canRequest = updates.canRequest !== undefined ? updates.canRequest : reason.can_request

      this.$nextTick(_ => {
        docendo.schoolAbsenceSubjectsUpdate(
          reason.id,
          reason.school_id,
          reason.name,
          reason.color,
          defaultPrivate,
          defaultOngoing,
          plannedAbsence,
          kmdAbsenceCodeId,
          sdAbsenceCodeId,
          canRequest,
          success => {
            if (success.status === 'error') {
              eventbus.toast = {text: success.message, 'success': false}
              return
            }
            this.updateAbsenceReason(success)
            if (trackEvent) {
              this.$store.dispatch('trackEvent', trackEvent)
            }
          },
          failure => {
            console.log({failure})
            if (errorMessage) {
              eventbus.toast = {text: errorMessage, 'success': false}
            }
          }
        )
      })
    },
    editAbsencePrivate (reason) {
      this.updateAbsenceReasonWithDefaults(reason, {
        defaultPrivate: !reason.default_private
      })
    },
    editAbsenceCanRequest (reason) {
      this.updateAbsenceReasonWithDefaults(reason, {
        canRequest: !reason.can_request
      }, null, 'absenceReasons_settings_canRequest')
    },
    editAbsenceOngoing (reason) {
      this.updateAbsenceReasonWithDefaults(reason, {
        defaultOngoing: !reason.default_ongoing
      }, null, 'absenceReasons_settings_defaultOngoing')
    },
    editAbsenceCode (reason, absenceCode, setDefaultOngoing, isSD) {
      const absenceCodeValue = absenceCode && absenceCode.trim() !== '' ? absenceCode.trim() : null
      const codeId = absenceCodeValue ? parseInt(absenceCodeValue, 10) : null

      const updates = {}

      if (isSD) {
        updates.sdAbsenceCodeId = codeId
        // SD codes har ikke allow_open property, så vi ignorerer default_ongoing logik for SD
      } else {
        updates.kmdAbsenceCodeId = codeId
        // Tjek om den valgte kode muliggør eller ikke muliggør default_ongoing
        const selectedCode = codeId ? this.absenceCodes.find(c => c.id === codeId) : null
        const shouldDisableDefaultOngoing = selectedCode && this.isAbsenceIntegrationActive && selectedCode.allow_open === false
        const shouldEnableDefaultOngoing = selectedCode && this.isAbsenceIntegrationActive && selectedCode.allow_open === true

        // Hvis koden ikke muliggør default_ongoing, nulstil den
        if (shouldDisableDefaultOngoing || setDefaultOngoing === false) {
          updates.defaultOngoing = false
        } else if (shouldEnableDefaultOngoing || setDefaultOngoing === true) {
          // Hvis koden muliggør default_ongoing, slå den til
          updates.defaultOngoing = true
        }
      }

      this.updateAbsenceReasonWithDefaults(reason, updates, 'Kunne ikke opdatere fraværskode')
    },
    updateAbsenceReason (updatedReason) {
      const idx = this.absenceReasons.findIndex(r => r.id === updatedReason.id)
      if (idx !== -1) {
        this.$set(this.absenceReasons, idx, updatedReason)
      }
      this.theUpdatingReasonId = null
    },
    toggleArchived (reason) {
      const isArchived = reason.archived
      this.theUpdatingReasonId = reason.id
      if (!isArchived) {
        docendo.schoolAbsenceSubjectsArchive(reason.school_id, reason.id,
          success => {
            if (success.status === 'error') {
              eventbus.toast = {text: success.message, 'success': false}
              return
            }
            this.updateAbsenceReason(success)
          },
          failure => {
            console.log('failed!')
          }
        )
      } else {
        docendo.schoolAbsenceSubjectsUnarchive(reason.school_id, reason.id,
          success => {
            this.updateAbsenceReason(success)
          },
          failure => {
            console.log('failed!')
          })
      }
    },
    setIsCreateAbsenceReason (bool) {
      this.isCreateAbsenceReason = bool
      if (!bool) {
        this.newReasonTitle = ''
        this.errors = {}
        this.isCreating = false
      }
    },
    readAbsenceReasons () {
      if (!this.schoolId) {
        return
      }
      this.isLoading = true
      docendo.schoolAbsenceSubjectsList(this.schoolId, true, success => {
        this.absenceReasons = success
        this.isLoading = false
      }, failure => {
        this.isLoading = false
      })
    },
    readAbsenceCodes () {
      if (!this.isAbsenceIntegrationActive || !this.schoolId) {
        return
      }
      if (this.salarySystem === 'SD') {
        // Hent SD absence codes
        docendo.listSilkeborgDataAbsenceCodes(this.schoolId, success => {
          this.sdAbsenceCodes = Array.isArray(success) ? success : []
        }, failure => {
          this.sdAbsenceCodes = []
        })
      } else {
        // Hent KMD absence codes
        this.listKmdOpusAbsenceCodes()
      }
    },
    listKmdOpusAbsenceCodes () {
      docendo.listKmdOpusAbsenceCodes(this.schoolId, success => {
        this.absenceCodes = Array.isArray(success) ? success : []
      }, failure => {
        this.absenceCodes = []
      })
    }
  },
  mounted () {
    this.readAbsenceReasons()
    this.readAbsenceCodes()
    this._keyListener = e => {
      if (e.key === 'n' && e.target.tagName.toUpperCase() === 'BODY') {
        this.setIsCreateAbsenceReason(true)
      }
    }

    document.addEventListener('keydown', this._keyListener)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this._keyListener)
  }
}
</script>

<style scoped>
.absence-reasons__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.absence-reasons__header .do-bttn--submit {
  padding: 0rem 2rem;
  height: 32px;
}

.do-modal__body {
  background: #edf0f5;
  max-height: calc(100vh - 105px);
  overflow-y: auto;
  overscroll-behavior: contain;
  min-width: 500px;
  padding: 2rem;
}

.do-modal__footer {
  gap: 1rem;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pad-1 {
  padding: 1rem !important;
}

</style>



// WEBPACK FOOTER //
// AbsenceReasons.vue?6933fbdc